import {
	BESTUHLUNG_OPTIONS,
	BOOKING_ARTS,
	QUELLEN,
	ZEITBLOCKS,
} from "@booking-hub/shared";
import { Save } from "lucide-react";

// ============================================================
// Booking Detail — Overview Tab (Bearbeiten)
// ============================================================

export function OverviewTab({
	booking,
	contact,
	editing,
	formData,
	onStartEdit,
	onSave,
	onCancel,
	onSetField,
	saving,
}: {
	booking: Record<string, unknown>;
	contact: Record<string, unknown> | null;
	editing: boolean;
	formData: Record<string, unknown>;
	onStartEdit: () => void;
	onSave: () => void;
	onCancel: () => void;
	onSetField: (k: string, v: unknown) => void;
	saving: boolean;
}) {
	const Field = ({
		label,
		field,
		type = "text",
	}: { label: string; field: string; type?: string }) => (
		<div>
			{/* biome-ignore lint/a11y/noLabelWithoutControl: label wraps control directly */}
			<label className="text-xs font-medium text-stone-500 block mb-1">
				{label}
				{editing ? (
					type === "select-art" ? (
						<select
							value={String(formData[field] ?? "")}
							onChange={(e) => onSetField(field, e.target.value)}
							className="input"
						>
							<option value="">—</option>
							{BOOKING_ARTS.map((a) => (
								<option key={a}>{a}</option>
							))}
						</select>
					) : type === "select-zeitblock" ? (
						<select
							value={String(formData[field] ?? "")}
							onChange={(e) => onSetField(field, e.target.value)}
							className="input"
						>
							<option value="">—</option>
							{ZEITBLOCKS.map((z) => (
								<option key={z}>{z}</option>
							))}
						</select>
					) : type === "select-bestuhlung" ? (
						<select
							value={String(formData[field] ?? "")}
							onChange={(e) => onSetField(field, e.target.value)}
							className="input"
						>
							<option value="">—</option>
							{BESTUHLUNG_OPTIONS.map((b) => (
								<option key={b}>{b}</option>
							))}
						</select>
					) : type === "select-quelle" ? (
						<select
							value={String(formData[field] ?? "")}
							onChange={(e) => onSetField(field, e.target.value)}
							className="input"
						>
							<option value="">—</option>
							{QUELLEN.map((q) => (
								<option key={q}>{q}</option>
							))}
						</select>
					) : type === "textarea" ? (
						<textarea
							value={String(formData[field] ?? "")}
							onChange={(e) => onSetField(field, e.target.value)}
							className="input min-h-[80px]"
						/>
					) : (
						<input
							type={type}
							value={String(formData[field] ?? "")}
							onChange={(e) =>
								onSetField(
									field,
									type === "number" ? Number(e.target.value) : e.target.value,
								)
							}
							className="input"
						/>
					)
				) : (
					<p className="text-sm text-stone-900">
						{String(booking[field] ?? "—")}
					</p>
				)}
			</label>
		</div>
	);

	return (
		<div className="space-y-6">
			<div className="flex justify-end gap-2">
				{editing ? (
					<>
						<button
							type="button"
							onClick={onCancel}
							className="btn-ghost text-sm"
						>
							Abbrechen
						</button>
						<button
							type="button"
							onClick={onSave}
							disabled={saving}
							className="btn-primary text-sm"
						>
							<Save size={14} /> Speichern
						</button>
					</>
				) : (
					<button
						type="button"
						onClick={onStartEdit}
						className="btn-secondary text-sm"
					>
						Bearbeiten
					</button>
				)}
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="card space-y-4">
					<h3 className="font-medium text-stone-700">Veranstaltung</h3>
					<Field label="Datum" field="datum" type="date" />
					<Field label="Datum Ende" field="datumEnde" type="date" />
					<Field label="Art" field="art" type="select-art" />
					<Field label="Personen" field="personen" type="number" />
					<Field label="Zeitblock" field="zeitblock" type="select-zeitblock" />
					<Field label="Uhrzeiten" field="uhrzeiten" />
					<Field
						label="Bestuhlung"
						field="bestuhlung"
						type="select-bestuhlung"
					/>
					<Field label="Quelle" field="quelle" type="select-quelle" />
					<Field label="Preis (€)" field="preis" type="number" />
				</div>

				<div className="card space-y-4">
					<h3 className="font-medium text-stone-700">Kontakt</h3>
					<Field label="Kunde" field="kunde" />
					<Field label="Firma" field="firma" />
					<Field label="E-Mail" field="kontaktEmail" type="email" />
					<Field label="Telefon" field="kontaktTel" />
					<Field label="Straße" field="strasse" />
					<Field label="PLZ" field="plz" />
					<Field label="Ort" field="ort" />
					{contact && (
						<div className="pt-2 border-t border-stone-100">
							<p className="text-xs text-stone-400">
								Verknüpfter Kontakt: {String(contact.name)}
							</p>
						</div>
					)}
				</div>
			</div>

			<div className="card space-y-4">
				<h3 className="font-medium text-stone-700">Details</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Field label="Sonderwünsche" field="sonderwuensche" type="textarea" />
					<Field label="Notizen" field="notizen" type="textarea" />
				</div>
				<Field label="Catering" field="catering" type="textarea" />
			</div>
		</div>
	);
}
